import React, { useContext, useEffect, useState } from "react";
import Button from "@mui/joy/Button";
import Modal from "@mui/joy/Modal";
import ModalClose from "@mui/joy/ModalClose";
import Typography from "@mui/joy/Typography";
import Sheet from "@mui/joy/Sheet";
import AuthContext from "../../contexts/authContext";
import { Box, Divider } from "@mui/joy";
import { FormControl, MenuItem, Select } from "@mui/material";
import NearMeIcon from "@mui/icons-material/NearMe";
import DeleteIcon from "@mui/icons-material/Delete";
import axiosInstance from "../../api/axios";
import toast from "react-hot-toast";

export default function ViewMobCustDetails({
  handleCloseViewCustomer,
  openViewCustomer,
  customerId,
  setCustomerId,
  deleted,
}) {
  const { userData, setRefresh } = useContext(AuthContext);

  const [viewData, setViewData] = useState(null);
  const [custSchemeData, setCustSchemeData] = useState([]);
  const [activeTab, setActiveTab] = useState("schemes");

  const role =
    typeof window !== "undefined" ? localStorage.getItem("role") : "";

  // SCHEME STATUS MODAL
  const [openConfirmSchemeComplete, setOpenConfirmSchemeComplete] =
    useState(false);
  const [schemeIdComp, setSchemeIdComp] = useState("");
  const [schemeStatus, setSchemeStatus] = useState("completed");

  // CUSTOMER STATUS MODAL
  const [openConfirmCustomerStatus, setOpenConfirmCustomerStatus] =
    useState(false);
  const [customerStatus, setCustomerStatus] = useState("completed");

  // ---------------------------
  // FETCH USER + SCHEMES
  // ---------------------------
  useEffect(() => {
    if (!customerId || !userData) return;

    const cust = userData?.customers?.find((item) => item._id === customerId);
    setViewData(cust || null);

    const schemeList =
      userData?.custSchemes?.filter((c) => c.customerId?._id === customerId) ||
      [];
    setCustSchemeData(schemeList);
  }, [userData, customerId]);

  // ---------------------------
  // IMAGE VIEW
  // ---------------------------
  const handleEnlargeImage = (img) => {
    if (!img) return;
    const imageUrl = `https://sakthiapi.mapsapp.in/uploads/${img}`;
    const newWindow = window.open();
    newWindow.document.write(
      `<img src="${imageUrl}" style="width:100%;object-fit:contain;" />`
    );
  };

  // ---------------------------
  // DELETE IMAGE
  // ---------------------------
  const handleDeleteCustomerImage = async () => {
    if (!viewData?.LocationImg) return toast.error("No image found");

    if (!window.confirm("Delete this image?")) return;

    try {
      const res = await axiosInstance.put(
        `/customers/deleteimage/${customerId}`
      );
      if (res.status === 200) {
        toast.success("Image deleted");
        setRefresh(true);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete image");
    }
  };

  // ---------------------------
  // SCHEME COMPLETE
  // ---------------------------
  const handleCompleteScheme = (id) => {
    setSchemeIdComp(id);
    setOpenConfirmSchemeComplete(true);
  };

  const handleCompleteSchemeConfirm = async () => {
    try {
      const res = await axiosInstance.put(
        `/custscheme/update/${schemeIdComp}`,
        {
          status: schemeStatus,
        }
      );

      if (res.status === 200) {
        toast.success("Scheme updated");
        setOpenConfirmSchemeComplete(false);
        setSchemeIdComp("");
        setRefresh(true);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to update scheme");
    }
  };

  // ---------------------------
  // CUSTOMER STATUS UPDATE
  // ---------------------------
  const handleConfirmCustomerStatus = async () => {
    try {
      const res = await axiosInstance.put(
        `/customers/updatestatus/${customerId}`,
        { status: customerStatus }
      );

      if (res.status === 200) {
        toast.success("Customer updated");
        setOpenConfirmCustomerStatus(false);
        handleCloseViewCustomer();
        setCustomerId("");
        setRefresh(true);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to update customer");
    }
  };

  if (!viewData) {
    // when data hasn't loaded yet
    return (
      <Modal
        open={openViewCustomer}
        onClose={handleCloseViewCustomer}
        sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <Sheet
          sx={{
            maxWidth: 400,
            width: "100%",
            bgcolor: "#FFF4F5",
            p: 2,
            m: 1,
            borderRadius: "md",
            boxShadow: "lg",
          }}
        >
          <ModalClose sx={{ m: 1 }} />
          <Typography level="h5">Loading customer…</Typography>
        </Sheet>
      </Modal>
    );
  }

  return (
    <>
      <Modal
        open={openViewCustomer}
        onClose={handleCloseViewCustomer}
        sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <Sheet
          sx={{
            maxWidth: 500,
            width: "100%",
            bgcolor: "#FFF4F5",
            p: 2,
            m: 1,
            borderRadius: "md",
            overflowY: "auto",
            height: "85vh",
            boxShadow: "lg",
          }}
        >
          <ModalClose sx={{ m: 1 }} />

          {/* NAME */}
          <Typography
            level="h4"
            sx={{ fontWeight: "bold", color: "#800001", mb: 1 }}
          >
            {viewData?.name}
          </Typography>

          {/* CONTACT + MAP + IMAGE + STATUS */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            {/* LEFT: mobile, map, thumbnail image, delete */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Typography sx={{ fontSize: 14 }}>{viewData?.mobile}</Typography>

              {viewData?.lat && viewData?.long && (
                <Button
                  size="sm"
                  component="a"
                  href={`https://www.google.com/maps/dir/?api=1&destination=${viewData.lat},${viewData.long}`}
                  target="_blank"
                  sx={{
                    background: "transparent",
                    "&:hover": { background: "transparent" },
                    p: 0,
                    minWidth: "auto",
                  }}
                >
                  <NearMeIcon sx={{ color: "#800001" }} />
                </Button>
              )}

              {viewData?.LocationImg && (
                <img
                  src={`https://sakthiapi.mapsapp.in/uploads/${viewData.LocationImg}`}
                  alt="Location"
                  onClick={() => handleEnlargeImage(viewData.LocationImg)}
                  style={{
                    width: "50px",
                    height: "50px",
                    borderRadius: 6,
                    border: "1px solid #800001",
                    objectFit: "cover",
                    cursor: "pointer",
                  }}
                />
              )}

              {/* Delete button only for admin & if img exists & not deleted */}
              {!deleted && role === "admin" && viewData?.LocationImg && (
                <Button
                  onClick={handleDeleteCustomerImage}
                  sx={{
                    background: "transparent",
                    "&:hover": { background: "transparent" },
                    p: 0,
                    minWidth: "auto",
                  }}
                >
                  <DeleteIcon sx={{ color: "#800001", fontSize: 20 }} />
                </Button>
              )}
            </Box>

            {/* RIGHT: status button / label */}
            <Box>
              {!deleted ? (
                <Button
                  sx={{
                    backgroundColor: "green",
                    color: "#fff",
                    fontSize: 12,
                    px: 2,
                    py: 0.6,
                    textTransform: "capitalize",
                    "&:hover": { backgroundColor: "#800001" },
                  }}
                  onClick={() => setOpenConfirmCustomerStatus(true)}
                >
                  {viewData?.status}
                </Button>
              ) : (
                <Typography
                  sx={{
                    background: "#800001",
                    color: "#fff",
                    px: 2,
                    py: 0.6,
                    borderRadius: 999,
                    fontSize: 12,
                    textTransform: "capitalize",
                  }}
                >
                  {viewData?.status}
                </Typography>
              )}
            </Box>
          </Box>

          {/* TABS */}
          <Box
            sx={{
              display: "flex",
              background: "#fff",
              borderRadius: 10,
              mt: 3,
              overflow: "hidden",
            }}
          >
            <Button
              fullWidth
              sx={{
                backgroundColor: activeTab === "schemes" ? "#800001" : "#fff",
                color: activeTab === "schemes" ? "#fff" : "#800001",
                borderRadius: 0,
                fontSize: 14,
              }}
              onClick={() => setActiveTab("schemes")}
            >
              Schemes
            </Button>
            <Button
              fullWidth
              sx={{
                backgroundColor: activeTab === "updates" ? "#800001" : "#fff",
                color: activeTab === "updates" ? "#fff" : "#800001",
                borderRadius: 0,
                fontSize: 14,
              }}
              onClick={() => setActiveTab("updates")}
            >
              Updates
            </Button>
          </Box>

          {/* --------------------- SCHEMES ---------------------- */}
          {activeTab === "schemes" && (
            <Box sx={{ mt: 2 }}>
              {custSchemeData.length === 0 && (
                <Typography sx={{ fontSize: 14 }}>No schemes found.</Typography>
              )}

              {custSchemeData
                .slice()
                .sort(
                  (a, b) => new Date(b.joiningDate) - new Date(a.joiningDate)
                )
                .map((item) => {
                  const paymentCount =
                    item.paymentsId?.filter(
                      (p) => p.amountPaid > 0 && p.amountPaid === item.amount
                    ).length || 0;

                  return (
                    <Box
                      key={item._id}
                      sx={{
                        mb: 2,
                        p: 2,
                        background: "#fff3f3",
                        borderRadius: 2,
                      }}
                    >
                      {/* Scheme header */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: "bold",
                              color: "#800001",
                              fontSize: 15,
                              textTransform: "uppercase",
                            }}
                          >
                            {item.schemeId?.schemeName || "Scheme"}
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: "#555" }}>
                            {item.joiningDate}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 12,
                              textTransform: "capitalize",
                              fontWeight: "bold",
                              color:
                                item.status === "active"
                                  ? "green"
                                  : item.status === "completed"
                                  ? "#800001"
                                  : "red",
                            }}
                          >
                            {item.status}
                          </Typography>

                          {!deleted &&
                            item.status === "active" &&
                            role !== "agent" && (
                              <Button
                                size="sm"
                                sx={{
                                  background: "#800001",
                                  color: "#fff",
                                  "&:hover": { background: "#800001" },
                                  fontSize: 11,
                                  px: 1.5,
                                  py: 0.4,
                                }}
                                onClick={() => handleCompleteScheme(item._id)}
                              >
                                Complete
                              </Button>
                            )}
                        </Box>
                      </Box>

                      <Divider sx={{ my: 1 }} />

                      {/* Payments summary */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: "bold",
                            color: "#800001",
                            fontSize: 13,
                          }}
                        >
                          Payments – {paymentCount} / {item.totalMonths}
                        </Typography>

                        <Typography
                          sx={{
                            fontWeight: "bold",
                            fontSize: 12,
                            textTransform: "capitalize",
                            color:
                              item.paymentStatus === "unpaid" ? "red" : "green",
                          }}
                        >
                          {item.paymentStatus || ""}
                        </Typography>
                      </Box>

                      {/* Payment list */}
                      {item.paymentsId?.length > 0 ? (
                        item.paymentsId.map((p) => (
                          <Box
                            key={p._id}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              py: 0.8,
                              borderBottom: "1px solid #eee",
                            }}
                          >
                            <Typography sx={{ fontSize: 12 }}>
                              {p.collectionDate}
                            </Typography>
                            <Typography
                              sx={{ fontWeight: "bold", fontSize: 13 }}
                            >
                              ₹{p.amountPaid}
                            </Typography>
                          </Box>
                        ))
                      ) : (
                        <Typography sx={{ fontSize: 13, mt: 0.5 }}>
                          No payments yet
                        </Typography>
                      )}
                    </Box>
                  );
                })}
            </Box>
          )}

          {/* --------------------- UPDATES ---------------------- */}
          {activeTab === "updates" && (
            <Box sx={{ mt: 2 }}>
              {viewData?.updates?.length > 0 ? (
                viewData.updates
                  .slice()
                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                  .map((u, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        p: 1.5,
                        mb: 1,
                        background: "#fff",
                        borderRadius: 2,
                      }}
                    >
                      <Typography sx={{ fontSize: 13 }}>{u.text}</Typography>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mt: 0.5,
                        }}
                      >
                        <Typography sx={{ color: "gray", fontSize: 11 }}>
                          {userData?.users?.find(
                            (usr) => usr._id === u.added_by
                          )?.name || "Unknown"}
                        </Typography>
                        <Typography sx={{ color: "gray", fontSize: 11 }}>
                          {new Date(u.date).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Box>
                  ))
              ) : (
                <Typography sx={{ fontSize: 14 }}>No updates found.</Typography>
              )}
            </Box>
          )}
        </Sheet>
      </Modal>

      {/* ----------------- SCHEME STATUS MODAL ---------------- */}
      <Modal
        open={openConfirmSchemeComplete}
        onClose={() => setOpenConfirmSchemeComplete(false)}
        sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <Sheet
          sx={{
            maxWidth: 300,
            width: "100%",
            bgcolor: "#FEDDDD",
            p: 3,
            borderRadius: "md",
            boxShadow: "lg",
          }}
        >
          <Typography
            level="h5"
            sx={{ textAlign: "center", mb: 2, fontWeight: "bold" }}
          >
            Change scheme status
          </Typography>

          <FormControl fullWidth sx={{ mt: 1 }}>
            <Select
              variant="standard"
              value={schemeStatus}
              onChange={(e) => setSchemeStatus(e.target.value)}
            >
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              sx={{ background: "#800001", color: "#fff" }}
              onClick={handleCompleteSchemeConfirm}
            >
              Submit
            </Button>
          </Box>
        </Sheet>
      </Modal>

      {/* ----------------- CUSTOMER STATUS MODAL ---------------- */}
      <Modal
        open={openConfirmCustomerStatus}
        onClose={() => setOpenConfirmCustomerStatus(false)}
        sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <Sheet
          sx={{
            maxWidth: 300,
            width: "100%",
            bgcolor: "#FEDDDD",
            p: 3,
            borderRadius: "md",
            boxShadow: "lg",
          }}
        >
          <Typography
            level="h5"
            sx={{ textAlign: "center", mb: 2, fontWeight: "bold" }}
          >
            Change customer status
          </Typography>

          <FormControl fullWidth sx={{ mt: 1 }}>
            <Select
              variant="standard"
              value={customerStatus}
              onChange={(e) => setCustomerStatus(e.target.value)}
            >
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              sx={{ background: "#800001", color: "#fff" }}
              onClick={handleConfirmCustomerStatus}
            >
              Submit
            </Button>
          </Box>
        </Sheet>
      </Modal>
    </>
  );
}
